import React, { useCallback, useEffect, useMemo, useState } from "react";

type LiveStatus = "Live" | "Scheduled" | "Completed" | "Cancelled";
type Platform =
  | "Website Live"
  | "Google Meet"
  | "Zoom"
  | "YouTube Live"
  | "Custom Link";

type PageMode = "list" | "form";

type LiveClass = {
  id: number;
  title: string;
  course: string;
  batch: string;
  teacher: string;
  subject: string;
  platform: Platform;
  meetingLink: string;
  roomName?: string;
  date: string;
  startTime: string;
  duration: number;
  status: LiveStatus;
  students: number;
  attendance: number;
  recordingEnabled: boolean;
  chatEnabled: boolean;
  waitingRoom: boolean;
  materials: string;
  description: string;
  recordingUrl?: string;
};

type LiveClassForm = Omit<
  LiveClass,
  "id" | "students" | "attendance" | "roomName" | "recordingUrl"
>;

type LiveClassStats = {
  totalClasses: number;
  liveNow: number;
  scheduled: number;
  completed: number;
  cancelled: number;
  totalAttendance: number;
};

type LiveClassAttendance = {
  id?: number;
  liveClassId?: number;
  studentId?: number;
  studentName?: string;
  joinedAt?: string;
  leftAt?: string;
  totalMinutes?: number;
  attendanceStatus?: string;
};

const API_BASE_URL = "https://api.mediitacademy.com/";
const LIVE_CLASSES_API = `${API_BASE_URL}/api/admin/live-classes`;

const emptyForm: LiveClassForm = {
  title: "",
  course: "NEET Complete Preparation",
  batch: "NEET 2026 Morning Batch",
  teacher: "Dr. Neha Sharma",
  subject: "Biology",
  platform: "Website Live",
  meetingLink: "",
  date: "",
  startTime: "",
  duration: 60,
  status: "Scheduled",
  recordingEnabled: true,
  chatEnabled: true,
  waitingRoom: true,
  materials: "",
  description: "",
};

const statuses = ["All", "Live", "Scheduled", "Completed", "Cancelled"];
const subjects = [
  "All",
  "Biology",
  "Physics",
  "Chemistry",
  "Mathematics",
  "Science",
];

const AdminLiveClasses: React.FC = () => {
  const [liveClasses, setLiveClasses] = useState<LiveClass[]>([]);
  const [todayClasses, setTodayClasses] = useState<LiveClass[]>([]);
  const [stats, setStats] = useState<LiveClassStats | null>(null);

  const [pageMode, setPageMode] = useState<PageMode>("list");
  const [editingId, setEditingId] = useState<number | null>(null);

  const [search, setSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("All");
  const [activeSubject, setActiveSubject] = useState("All");

  const [form, setForm] = useState<LiveClassForm>(emptyForm);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceClassTitle, setAttendanceClassTitle] = useState("");
  const [attendanceList, setAttendanceList] = useState<LiveClassAttendance[]>([]);

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("adminToken");

    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const apiRequest = async <T,>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> => {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...(options.headers || {}),
      },
    });

    const text = await response.text();

    let data: any = null;

    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = text;
      }
    }

    if (!response.ok) {
      const message =
        typeof data === "string"
          ? data
          : data?.message || data?.error || "Something went wrong";
      throw new Error(message);
    }

    return data as T;
  };

  const normalizeStatus = (value: any): LiveStatus => {
    const status = String(value || "Scheduled").toLowerCase();

    if (status === "live") return "Live";
    if (status === "completed") return "Completed";
    if (status === "cancelled" || status === "canceled") return "Cancelled";

    return "Scheduled";
  };

  const normalizeTime = (value: any) => {
    if (!value) return "";
    return String(value).slice(0, 5);
  };

  const normalizeClass = (item: any): LiveClass => {
    return {
      id: Number(item.id),
      title: item.title || "",
      course: item.course || "",
      batch: item.batch || "",
      teacher: item.teacher || "",
      subject: item.subject || "",
      platform: (item.platform || "Website Live") as Platform,
      meetingLink: item.meetingLink || item.meeting_link || "",
      roomName: item.roomName || item.room_name || "",
      date: item.date || item.classDate || item.class_date || "",
      startTime: normalizeTime(item.startTime || item.start_time),
      duration: Number(item.duration || 60),
      status: normalizeStatus(item.status),
      students: Number(item.students || 0),
      attendance: Number(item.attendance || 0),
      recordingEnabled:
        item.recordingEnabled ?? item.recording_enabled ?? true,
      chatEnabled: item.chatEnabled ?? item.chat_enabled ?? true,
      waitingRoom: item.waitingRoom ?? item.waiting_room ?? true,
      materials: item.materials || "",
      description: item.description || "",
      recordingUrl: item.recordingUrl || item.recording_url || "",
    };
  };

  const toApiPayload = (value: LiveClassForm) => {
    return {
      title: value.title,
      course: value.course,
      batch: value.batch,
      teacher: value.teacher,
      subject: value.subject,
      platform: value.platform,
      meetingLink: value.meetingLink,
      date: value.date,
      startTime: value.startTime,
      duration: Number(value.duration),
      status: value.status,
      recordingEnabled: value.recordingEnabled,
      chatEnabled: value.chatEnabled,
      waitingRoom: value.waitingRoom,
      materials: value.materials,
      description: value.description,
    };
  };

  const fetchLiveClasses = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await apiRequest<any[]>(LIVE_CLASSES_API);
      setLiveClasses(Array.isArray(data) ? data.map(normalizeClass) : []);
    } catch (err: any) {
      setError(err.message || "Live classes load nahi ho paayi.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiRequest<LiveClassStats>(`${LIVE_CLASSES_API}/stats`);
      setStats(data);
    } catch {
      setStats(null);
    }
  }, []);

  const fetchTodayClasses = useCallback(async () => {
    try {
      const data = await apiRequest<any[]>(`${LIVE_CLASSES_API}/today`);
      setTodayClasses(Array.isArray(data) ? data.map(normalizeClass) : []);
    } catch {
      setTodayClasses([]);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.allSettled([
      fetchLiveClasses(),
      fetchStats(),
      fetchTodayClasses(),
    ]);
  }, [fetchLiveClasses, fetchStats, fetchTodayClasses]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const filteredClasses = useMemo(() => {
    return liveClasses.filter((item) => {
      const matchStatus = activeStatus === "All" || item.status === activeStatus;
      const matchSubject =
        activeSubject === "All" || item.subject === activeSubject;

      const searchText = search.toLowerCase().trim();

      const matchSearch =
        !searchText ||
        item.title.toLowerCase().includes(searchText) ||
        item.course.toLowerCase().includes(searchText) ||
        item.batch.toLowerCase().includes(searchText) ||
        item.teacher.toLowerCase().includes(searchText) ||
        item.subject.toLowerCase().includes(searchText);

      return matchStatus && matchSubject && matchSearch;
    });
  }, [liveClasses, search, activeStatus, activeSubject]);

  const avgAttendance = useMemo(() => {
    const validClasses = liveClasses.filter((item) => item.students > 0);

    if (validClasses.length === 0) return "0%";

    const average =
      validClasses.reduce((total, item) => {
        return total + (item.attendance / item.students) * 100;
      }, 0) / validClasses.length;

    return `${Math.round(average)}%`;
  }, [liveClasses]);

  const openCreatePage = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEditPage = (liveClass: LiveClass) => {
    setEditingId(liveClass.id);
    setForm({
      title: liveClass.title,
      course: liveClass.course,
      batch: liveClass.batch,
      teacher: liveClass.teacher,
      subject: liveClass.subject,
      platform: liveClass.platform,
      meetingLink: liveClass.meetingLink,
      date: liveClass.date,
      startTime: liveClass.startTime,
      duration: liveClass.duration,
      status: liveClass.status,
      recordingEnabled: liveClass.recordingEnabled,
      chatEnabled: liveClass.chatEnabled,
      waitingRoom: liveClass.waitingRoom,
      materials: liveClass.materials,
      description: liveClass.description,
    });
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToList = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPageMode("list");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSaveClass = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !form.title.trim() ||
      !form.course.trim() ||
      !form.batch.trim() ||
      !form.teacher.trim() ||
      !form.subject.trim() ||
      !form.date ||
      !form.startTime
    ) {
      alert("Please fill title, course, batch, teacher, subject, date and time.");
      return;
    }

    if (!form.duration || Number(form.duration) < 10) {
      alert("Duration minimum 10 minutes honi chahiye.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      if (editingId) {
        await apiRequest(`${LIVE_CLASSES_API}/${editingId}`, {
          method: "PUT",
          body: JSON.stringify(toApiPayload(form)),
        });
      } else {
        await apiRequest(LIVE_CLASSES_API, {
          method: "POST",
          body: JSON.stringify(toApiPayload(form)),
        });
      }

      await refreshAll();
      backToList();
    } catch (err: any) {
      alert(err.message || "Class save nahi ho paayi.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    const confirmDelete = window.confirm(
      "Are you sure? Ye live class delete ho jayegi."
    );

    if (!confirmDelete) return;

    try {
      await apiRequest(`${LIVE_CLASSES_API}/${id}`, {
        method: "DELETE",
      });

      await refreshAll();
    } catch (err: any) {
      alert(err.message || "Live class delete nahi ho paayi.");
    }
  };

  const handleClassAction = async (
    id: number,
    action: "start" | "end" | "cancel"
  ) => {
    const confirmMessage =
      action === "start"
        ? "Class ko LIVE start karna hai?"
        : action === "end"
        ? "Class ko Completed mark karna hai?"
        : "Class cancel karni hai?";

    if (!window.confirm(confirmMessage)) return;

    try {
      await apiRequest(`${LIVE_CLASSES_API}/${id}/${action}`, {
        method: "POST",
      });

      await refreshAll();
    } catch (err: any) {
      alert(err.message || "Action complete nahi ho paaya.");
    }
  };

  const handleUploadRecording = async (classId?: number) => {
    const id =
      classId ||
      Number(window.prompt("Live class ID daalo jisme recording upload karni hai"));

    if (!id) return;

    const recordingUrl = window.prompt("Recording URL paste karo");

    if (!recordingUrl || !recordingUrl.trim()) return;

    try {
      await apiRequest(`${LIVE_CLASSES_API}/${id}/recording`, {
        method: "POST",
        body: JSON.stringify({ recordingUrl }),
      });

      await refreshAll();
      alert("Recording uploaded successfully.");
    } catch (err: any) {
      alert(err.message || "Recording upload nahi ho paayi.");
    }
  };

  const handleViewAttendance = async (liveClass: LiveClass) => {
    setAttendanceModalOpen(true);
    setAttendanceLoading(true);
    setAttendanceClassTitle(liveClass.title);
    setAttendanceList([]);

    try {
      const data = await apiRequest<LiveClassAttendance[]>(
        `${LIVE_CLASSES_API}/${liveClass.id}/attendance`
      );

      setAttendanceList(Array.isArray(data) ? data : []);
    } catch (err: any) {
      alert(err.message || "Attendance load nahi ho paayi.");
    } finally {
      setAttendanceLoading(false);
    }
  };

  const downloadCSV = (filename: string, rows: string[][]) => {
    const csvContent = rows
      .map((row) =>
        row
          .map((cell) => {
            const value = String(cell ?? "");
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleExportClassSummary = () => {
    const rows = [
      [
        "ID",
        "Title",
        "Course",
        "Batch",
        "Teacher",
        "Subject",
        "Date",
        "Time",
        "Duration",
        "Platform",
        "Status",
        "Students",
        "Attendance",
        "Recording URL",
      ],
      ...filteredClasses.map((item) => [
        item.id.toString(),
        item.title,
        item.course,
        item.batch,
        item.teacher,
        item.subject,
        item.date,
        item.startTime,
        `${item.duration} min`,
        item.platform,
        item.status,
        item.students.toString(),
        item.attendance.toString(),
        item.recordingUrl || "",
      ]),
    ];

    downloadCSV("live-classes-summary.csv", rows);
  };

  const handleExportAttendance = () => {
    if (attendanceList.length === 0) {
      alert("Pehle kisi class ki attendance open karo.");
      return;
    }

    const rows = [
      [
        "Student ID",
        "Student Name",
        "Joined At",
        "Left At",
        "Total Minutes",
        "Status",
      ],
      ...attendanceList.map((item) => [
        String(item.studentId || ""),
        item.studentName || "",
        item.joinedAt || "",
        item.leftAt || "",
        String(item.totalMinutes || 0),
        item.attendanceStatus || "",
      ]),
    ];

    downloadCSV("live-class-attendance.csv", rows);
  };

  const handleToolAction = (tool: string) => {
    if (tool === "Schedule Live Class") {
      openCreatePage();
      return;
    }

    if (tool === "Upload Recording") {
      handleUploadRecording();
      return;
    }

    if (tool === "Download Attendance") {
      handleExportClassSummary();
      return;
    }

    if (tool === "Send Class Reminder") {
      alert(
        "Iske liye backend me endpoint add karo: POST /api/admin/live-classes/{id}/send-reminder"
      );
      return;
    }

    if (tool === "Add Study Material") {
      alert(
        "Abhi materials text field se save ho raha hai. File upload ke liye multipart API add karni hogi."
      );
      return;
    }

    if (tool === "Create Doubt Session") {
      setForm({
        ...emptyForm,
        title: "Doubt Solving Session",
        description: "Special live doubt solving class.",
      });
      setEditingId(null);
      setPageMode("form");
      return;
    }
  };

  if (pageMode === "form") {
    return (
      <div className="w-full max-w-full min-w-0 overflow-hidden space-y-5 sm:space-y-6">
        <section className="relative overflow-hidden rounded-[24px] bg-slate-950 p-5 text-white shadow-xl shadow-slate-300/40 sm:rounded-[30px] sm:p-8">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

          <div className="relative flex min-w-0 flex-col justify-between gap-5 lg:flex-row lg:items-center">
            <div className="min-w-0">
              <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
                {editingId ? "Edit Live Class" : "Schedule Live Class"}
              </div>

              <h1 className="max-w-3xl text-2xl font-black tracking-tight sm:text-4xl">
                {editingId
                  ? "Update live class details"
                  : "Create new live class"}
              </h1>

              <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
                Class title, teacher, batch, platform, timing aur security
                settings yaha se manage karo.
              </p>
            </div>

            <button
              type="button"
              onClick={backToList}
              className="w-fit shrink-0 rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
            >
              ← Back to Live Classes
            </button>
          </div>
        </section>

        <form
          onSubmit={handleSaveClass}
          className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_340px]"
        >
          <section className="min-w-0 space-y-6">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Class Details
              </h2>

              <div className="mt-5 grid min-w-0 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FormInput
                    label="Live Class Title"
                    value={form.title}
                    placeholder="Example: NEET Biology Human Physiology"
                    onChange={(value) => setForm({ ...form, title: value })}
                  />
                </div>

                <SelectInput
                  label="Course"
                  value={form.course}
                  options={[
                    "NEET Complete Preparation",
                    "JEE Main + Advanced",
                    "Class 8 Science Foundation",
                    "Class 7 Maths Booster",
                  ]}
                  onChange={(value) => setForm({ ...form, course: value })}
                />

                <SelectInput
                  label="Batch"
                  value={form.batch}
                  options={[
                    "NEET 2026 Morning Batch",
                    "JEE Main Target Batch",
                    "Class 8 Science Foundation",
                    "Class 7 Maths Booster",
                  ]}
                  onChange={(value) => setForm({ ...form, batch: value })}
                />

                <SelectInput
                  label="Teacher"
                  value={form.teacher}
                  options={[
                    "Dr. Neha Sharma",
                    "Prof. Raman Sir",
                    "Pooja Singh",
                    "Amit Kumar",
                    "Maical Doe",
                  ]}
                  onChange={(value) => setForm({ ...form, teacher: value })}
                />

                <SelectInput
                  label="Subject"
                  value={form.subject}
                  options={[
                    "Biology",
                    "Physics",
                    "Chemistry",
                    "Mathematics",
                    "Science",
                  ]}
                  onChange={(value) => setForm({ ...form, subject: value })}
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Platform & Schedule
              </h2>

              <div className="mt-5 grid min-w-0 gap-4 sm:grid-cols-2">
                <SelectInput
                  label="Platform"
                  value={form.platform}
                  options={[
                    "Website Live",
                    "Google Meet",
                    "Zoom",
                    "YouTube Live",
                    "Custom Link",
                  ]}
                  onChange={(value) =>
                    setForm({ ...form, platform: value as Platform })
                  }
                />

                <FormInput
                  label="Meeting / Class Link"
                  value={form.meetingLink}
                  placeholder="Website Live hai to blank bhi rakh sakte ho"
                  onChange={(value) =>
                    setForm({ ...form, meetingLink: value })
                  }
                />

                <FormInput
                  label="Class Date"
                  type="date"
                  value={form.date}
                  placeholder=""
                  onChange={(value) => setForm({ ...form, date: value })}
                />

                <FormInput
                  label="Start Time"
                  type="time"
                  value={form.startTime}
                  placeholder=""
                  onChange={(value) => setForm({ ...form, startTime: value })}
                />

                <FormInput
                  label="Duration In Minutes"
                  type="number"
                  value={form.duration.toString()}
                  placeholder="60"
                  onChange={(value) =>
                    setForm({ ...form, duration: Number(value) })
                  }
                />

                <SelectInput
                  label="Status"
                  value={form.status}
                  options={["Scheduled", "Live", "Completed", "Cancelled"]}
                  onChange={(value) =>
                    setForm({ ...form, status: value as LiveStatus })
                  }
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Materials & Description
              </h2>

              <div className="mt-5">
                <FormInput
                  label="Study Material / Notes"
                  value={form.materials}
                  placeholder="Example: Human Physiology Notes.pdf"
                  onChange={(value) => setForm({ ...form, materials: value })}
                />
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Description
                </label>

                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Write class description..."
                  rows={5}
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
                />
              </div>
            </div>
          </section>

          <aside className="min-w-0 space-y-6">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
              <h2 className="text-xl font-black text-slate-950">
                Class Settings
              </h2>

              <div className="mt-5 grid gap-3 sm:grid-cols-2 2xl:grid-cols-1">
                <ToggleRow
                  title="Enable Recording"
                  description="Class complete hone ke baad recording available hogi"
                  checked={form.recordingEnabled}
                  onChange={(value) =>
                    setForm({ ...form, recordingEnabled: value })
                  }
                />

                <ToggleRow
                  title="Enable Chat"
                  description="Students class ke during chat kar sakenge"
                  checked={form.chatEnabled}
                  onChange={(value) => setForm({ ...form, chatEnabled: value })}
                />

                <ToggleRow
                  title="Waiting Room"
                  description="Teacher approval ke baad student join karega"
                  checked={form.waitingRoom}
                  onChange={(value) => setForm({ ...form, waitingRoom: value })}
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
              <h2 className="text-xl font-black text-slate-950">
                Live Class Preview
              </h2>

              <div className="mt-5 rounded-2xl bg-slate-50 p-4">
                <div className="flex flex-wrap gap-2">
                  <StatusBadge status={form.status} />
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                    {form.platform}
                  </span>
                </div>

                <h3 className="mt-4 break-words text-sm font-black leading-6 text-slate-950">
                  {form.title || "Live class title preview..."}
                </h3>

                <p className="mt-2 break-words text-xs font-bold text-slate-500">
                  {form.batch}
                </p>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <PreviewBox label="Date" value={form.date || "--"} />
                  <PreviewBox label="Time" value={form.startTime || "--"} />
                  <PreviewBox label="Duration" value={`${form.duration} min`} />
                  <PreviewBox label="Teacher" value={form.teacher} />
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-xl sm:rounded-[28px] 2xl:sticky 2xl:bottom-5">
              <div className="grid gap-3 sm:grid-cols-3 2xl:grid-cols-1">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? "Saving..."
                    : editingId
                    ? "Update Class"
                    : "Schedule Class"}
                </button>

                <button
                  type="button"
                  onClick={() => setForm({ ...form, status: "Scheduled" })}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  Save as Scheduled
                </button>

                <button
                  type="button"
                  onClick={backToList}
                  className="rounded-2xl bg-slate-100 px-5 py-3 text-center text-sm font-black text-slate-700 hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </aside>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden space-y-5 sm:space-y-6">
      <section className="relative overflow-hidden rounded-[24px] bg-slate-950 p-5 text-white shadow-xl shadow-slate-300/40 sm:rounded-[30px] sm:p-8">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

        <div className="relative flex min-w-0 flex-col justify-between gap-5 xl:flex-row xl:items-center">
          <div className="min-w-0">
            <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
              Live Classes Management
            </div>

            <h1 className="max-w-3xl text-2xl font-black tracking-tight sm:text-4xl">
              Manage live classes and recordings
            </h1>

            <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              Admin yaha se live class schedule, teacher assign, batch assign,
              recording aur attendance manage kar sakta hai.
            </p>

            {error && (
              <p className="mt-4 rounded-2xl bg-red-500/15 px-4 py-3 text-sm font-bold text-red-100">
                {error}
              </p>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap gap-3">
            <button
              onClick={() => handleUploadRecording()}
              className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15"
            >
              Upload Recording
            </button>

            <button
              onClick={handleExportClassSummary}
              className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15"
            >
              Export Attendance
            </button>

            <button
              onClick={openCreatePage}
              className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
            >
              + Schedule Class
            </button>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Classes"
          value={(stats?.totalClasses ?? liveClasses.length).toString()}
          note="All scheduled classes"
        />

        <StatCard
          title="Live Now"
          value={(
            stats?.liveNow ??
            liveClasses.filter((item) => item.status === "Live").length
          ).toString()}
          note="Currently running"
        />

        <StatCard
          title="Scheduled"
          value={(
            stats?.scheduled ??
            liveClasses.filter((item) => item.status === "Scheduled").length
          ).toString()}
          note="Upcoming classes"
        />

        <StatCard
          title="Avg Attendance"
          value={avgAttendance}
          note={`${stats?.totalAttendance ?? 0} total joins`}
        />
      </section>

      <section className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
        <div className="grid min-w-0 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[1fr_1fr_1.5fr]">
          <FilterButtons
            title="Status"
            options={statuses}
            active={activeStatus}
            onChange={setActiveStatus}
          />

          <FilterButtons
            title="Subject"
            options={subjects}
            active={activeSubject}
            onChange={setActiveSubject}
          />

          <div className="min-w-0 md:col-span-2 xl:col-span-1">
            <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
              Search
            </p>

            <div className="flex h-12 min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search class, batch, teacher..."
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white shadow-sm sm:rounded-[28px]">
          <div className="flex flex-col justify-between gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-xl font-black text-slate-950">
                All Live Classes
              </h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                {loading ? "Loading..." : `${filteredClasses.length} classes found`}
              </p>
            </div>

            <button
              onClick={openCreatePage}
              className="w-fit rounded-2xl bg-blue-600 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-700"
            >
              + Schedule New
            </button>
          </div>

          <div className="hidden max-w-full overflow-x-auto 2xl:block">
            <table className="w-full min-w-[1120px] text-left">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-4">Class</th>
                  <th className="px-5 py-4">Teacher</th>
                  <th className="px-5 py-4">Schedule</th>
                  <th className="px-5 py-4">Attendance</th>
                  <th className="px-5 py-4">Platform</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredClasses.map((item) => {
                  const attendancePercent =
                    item.students > 0
                      ? Math.round((item.attendance / item.students) * 100)
                      : 0;

                  return (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                    >
                      <td className="px-5 py-4">
                        <p className="max-w-sm text-sm font-black leading-5 text-slate-950">
                          {item.title}
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-400">
                          {item.subject} • {item.batch}
                        </p>
                        {item.roomName && (
                          <p className="mt-1 max-w-sm break-words text-[11px] font-bold text-slate-400">
                            Room: {item.roomName}
                          </p>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <p className="text-sm font-black text-slate-800">
                          {item.teacher}
                        </p>
                        <p className="text-xs font-bold text-slate-400">
                          {item.course}
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <p className="text-sm font-black text-slate-800">
                          {item.date}
                        </p>
                        <p className="text-xs font-bold text-slate-400">
                          {item.startTime} • {item.duration} min
                        </p>
                      </td>

                      <td className="px-5 py-4">
                        <div className="min-w-[110px]">
                          <div className="mb-2 flex justify-between text-xs font-black text-slate-500">
                            <span>
                              {item.attendance}/{item.students}
                            </span>
                            <span>{attendancePercent}%</span>
                          </div>

                          <div className="h-2 rounded-full bg-slate-100">
                            <div
                              className={`h-2 rounded-full ${
                                attendancePercent >= 80
                                  ? "bg-emerald-500"
                                  : attendancePercent >= 60
                                  ? "bg-blue-600"
                                  : "bg-orange-500"
                              }`}
                              style={{ width: `${attendancePercent}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                          {item.platform}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge status={item.status} />
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex flex-wrap justify-end gap-2">
                          {item.status === "Scheduled" && (
                            <button
                              onClick={() =>
                                handleClassAction(item.id, "start")
                              }
                              className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 hover:bg-emerald-100"
                            >
                              Start
                            </button>
                          )}

                          {item.status === "Live" && (
                            <button
                              onClick={() => handleClassAction(item.id, "end")}
                              className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-black text-blue-700 hover:bg-blue-100"
                            >
                              End
                            </button>
                          )}

                          {item.status === "Scheduled" && (
                            <button
                              onClick={() =>
                                handleClassAction(item.id, "cancel")
                              }
                              className="rounded-xl border border-orange-100 bg-orange-50 px-3 py-2 text-xs font-black text-orange-700 hover:bg-orange-100"
                            >
                              Cancel
                            </button>
                          )}

                          <button
                            onClick={() => handleViewAttendance(item)}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100"
                          >
                            Attendance
                          </button>

                          <button
                            onClick={() => handleUploadRecording(item.id)}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100"
                          >
                            Recording
                          </button>

                          <button
                            onClick={() => openEditPage(item)}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(item.id)}
                            className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-black text-red-600 hover:bg-red-100"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!loading && filteredClasses.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-5 py-10 text-center text-sm font-bold text-slate-500"
                    >
                      No live classes found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="grid gap-4 p-4 sm:p-5 2xl:hidden">
            {filteredClasses.map((item) => (
              <div
                key={item.id}
                className="min-w-0 rounded-[22px] border border-slate-100 bg-slate-50 p-4"
              >
                <div className="mb-3 flex flex-wrap gap-2">
                  <StatusBadge status={item.status} />
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                    {item.platform}
                  </span>
                </div>

                <h3 className="break-words text-sm font-black leading-5 text-slate-950">
                  {item.title}
                </h3>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <InfoBox label="Teacher" value={item.teacher} />
                  <InfoBox label="Batch" value={item.batch} />
                  <InfoBox label="Date" value={item.date} />
                  <InfoBox label="Time" value={item.startTime} />
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  {item.status === "Scheduled" && (
                    <button
                      onClick={() => handleClassAction(item.id, "start")}
                      className="rounded-2xl bg-emerald-50 px-4 py-2.5 text-xs font-black text-emerald-700 hover:bg-emerald-100"
                    >
                      Start
                    </button>
                  )}

                  {item.status === "Live" && (
                    <button
                      onClick={() => handleClassAction(item.id, "end")}
                      className="rounded-2xl bg-blue-50 px-4 py-2.5 text-xs font-black text-blue-700 hover:bg-blue-100"
                    >
                      End
                    </button>
                  )}

                  <button
                    onClick={() => handleViewAttendance(item)}
                    className="rounded-2xl bg-white px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-100"
                  >
                    Attendance
                  </button>

                  <button
                    onClick={() => openEditPage(item)}
                    className="rounded-2xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-600"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(item.id)}
                    className="rounded-2xl bg-red-50 px-4 py-2.5 text-xs font-black text-red-600 hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}

            {!loading && filteredClasses.length === 0 && (
              <div className="rounded-2xl bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">
                No live classes found.
              </div>
            )}
          </div>
        </div>

        <aside className="min-w-0 space-y-6">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">Admin Tools</h2>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 2xl:grid-cols-1">
              {[
                "Schedule Live Class",
                "Upload Recording",
                "Send Class Reminder",
                "Download Attendance",
                "Add Study Material",
                "Create Doubt Session",
              ].map((item) => (
                <button
                  key={item}
                  onClick={() => handleToolAction(item)}
                  className="group flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  <span className="break-words">{item}</span>
                  <span className="shrink-0 transition group-hover:translate-x-1">
                    →
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">Today Classes</h2>

            <div className="mt-5 space-y-4">
              {(todayClasses.length > 0 ? todayClasses : liveClasses.slice(0, 4))
                .slice(0, 4)
                .map((item) => (
                  <div
                    key={item.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 p-4"
                  >
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <p className="break-words text-sm font-black leading-5 text-slate-800">
                        {item.title}
                      </p>
                      <StatusBadge status={item.status} />
                    </div>

                    <p className="mt-2 text-xs font-bold text-slate-400">
                      {item.startTime} • {item.teacher}
                    </p>
                  </div>
                ))}

              {liveClasses.length === 0 && !loading && (
                <div className="rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-500">
                  Aaj koi class nahi hai.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">
              Class Performance
            </h2>

            <div className="mt-5 space-y-4">
              <QualityItem
                label="Average Attendance"
                value={avgAttendance}
                color="bg-blue-600"
              />
              <QualityItem
                label="Completed Classes"
                value={`${stats?.completed ?? 0}`}
                color="bg-emerald-500"
              />
              <QualityItem
                label="Cancelled Classes"
                value={`${stats?.cancelled ?? 0}`}
                color="bg-orange-500"
              />
            </div>
          </div>
        </aside>
      </section>

      {attendanceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
              <div>
                <h2 className="text-xl font-black text-slate-950">
                  Attendance
                </h2>
                <p className="mt-1 text-sm font-bold text-slate-500">
                  {attendanceClassTitle}
                </p>
              </div>

              <button
                onClick={() => setAttendanceModalOpen(false)}
                className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>

            <div className="max-h-[65vh] overflow-auto p-5">
              <div className="mb-4 flex flex-wrap justify-between gap-3">
                <p className="text-sm font-black text-slate-600">
                  Total joined: {attendanceList.length}
                </p>

                <button
                  onClick={handleExportAttendance}
                  className="rounded-2xl bg-blue-600 px-4 py-2 text-xs font-black text-white hover:bg-blue-700"
                >
                  Export CSV
                </button>
              </div>

              {attendanceLoading ? (
                <div className="rounded-2xl bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">
                  Attendance loading...
                </div>
              ) : attendanceList.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">
                  Abhi attendance record nahi mila.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                        <th className="px-4 py-3">Student</th>
                        <th className="px-4 py-3">Joined</th>
                        <th className="px-4 py-3">Left</th>
                        <th className="px-4 py-3">Minutes</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {attendanceList.map((item, index) => (
                        <tr
                          key={`${item.studentId}-${index}`}
                          className="border-b border-slate-100 last:border-0"
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-black text-slate-800">
                              {item.studentName || "Student"}
                            </p>
                            <p className="text-xs font-bold text-slate-400">
                              ID: {item.studentId || "--"}
                            </p>
                          </td>

                          <td className="px-4 py-3 text-sm font-bold text-slate-600">
                            {item.joinedAt || "--"}
                          </td>

                          <td className="px-4 py-3 text-sm font-bold text-slate-600">
                            {item.leftAt || "--"}
                          </td>

                          <td className="px-4 py-3 text-sm font-black text-slate-800">
                            {item.totalMinutes || 0}
                          </td>

                          <td className="px-4 py-3">
                            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                              {item.attendanceStatus || "PRESENT"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({
  title,
  value,
  note,
}: {
  title: string;
  value: string;
  note: string;
}) => {
  return (
    <div className="min-w-0 rounded-[22px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[24px]">
      <p className="text-sm font-bold text-slate-500">{title}</p>
      <h2 className="mt-2 text-3xl font-black text-slate-950">{value}</h2>
      <p className="mt-1 text-xs font-bold text-emerald-600">{note}</p>
    </div>
  );
};

const FilterButtons = ({
  title,
  options,
  active,
  onChange,
}: {
  title: string;
  options: string[];
  active: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="min-w-0">
      <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
        {title}
      </p>

      <div className="flex flex-wrap gap-2">
        {options.map((item) => (
          <button
            key={item}
            onClick={() => onChange(item)}
            className={`rounded-2xl px-3 py-2 text-xs font-black transition ${
              active === item
                ? "bg-slate-950 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: LiveStatus }) => {
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-black ${
        status === "Live"
          ? "bg-red-50 text-red-700"
          : status === "Scheduled"
          ? "bg-blue-50 text-blue-700"
          : status === "Completed"
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
};

const InfoBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="min-w-0 rounded-2xl bg-white p-3">
      <p className="text-xs font-bold text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-black text-slate-900">
        {value || "--"}
      </p>
    </div>
  );
};

const QualityItem = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) => {
  const isPercent = value.includes("%");
  const width = isPercent ? value : "60%";

  return (
    <div>
      <div className="mb-2 flex justify-between gap-3 text-sm font-black text-slate-700">
        <span>{label}</span>
        <span>{value}</span>
      </div>

      <div className="h-2 rounded-full bg-slate-100">
        <div className={`h-2 rounded-full ${color}`} style={{ width }} />
      </div>
    </div>
  );
};

const FormInput = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  placeholder: string;
  type?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="min-w-0">
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>

      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
      />
    </div>
  );
};

const SelectInput = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => {
  return (
    <div className="min-w-0">
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
      >
        {options.map((item) => (
          <option key={item}>{item}</option>
        ))}
      </select>
    </div>
  );
};

const PreviewBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="min-w-0 rounded-2xl bg-white p-3">
      <p className="text-xs font-bold text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-black text-slate-950">
        {value || "--"}
      </p>
    </div>
  );
};

const ToggleRow = ({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) => {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex w-full min-w-0 items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-left hover:bg-slate-100"
    >
      <div className="min-w-0">
        <p className="break-words text-sm font-black text-slate-950">{title}</p>
        <p className="mt-1 break-words text-xs font-bold text-slate-400">
          {description}
        </p>
      </div>

      <span
        className={`flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition ${
          checked ? "bg-blue-600" : "bg-slate-300"
        }`}
      >
        <span
          className={`h-5 w-5 rounded-full bg-white transition ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </span>
    </button>
  );
};

export default AdminLiveClasses;